import { useEffect, useState } from "react";

/**
 * Desktop-only gate.
 *
 * The check is pure, synchronous reads of `navigator.userAgent` and
 * `window.innerWidth` — it never needed to be asynchronous. It used to run in a
 * `useEffect` behind an `isLoading` flag, which forced App to render a full-screen
 * loader before the router even existed: one more Zen mark, at one more moment, at
 * the very front of every boot. Computing it in the initial state removes that
 * screen entirely; `isDesktop` is correct on the first render.
 */
function detectDesktop(): boolean {
  if (typeof window === "undefined") return true;
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobile =
    /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
  const isTablet = /ipad|android(?!.*mobile)/i.test(userAgent);
  const screenWidth = window.innerWidth;

  // Consider desktop if:
  // 1. Not mobile or tablet user agent
  // 2. Screen width is at least 1024px (laptop/desktop size)
  return !isMobile && !isTablet && screenWidth >= 1024;
}

/**
 * Pages that open on any screen, past the desktop-only gate. A project review link
 * (`/review/:token`) goes to a buyer's client, who often opens it on a phone; the page is a
 * single column with no app shell, so the gate only stood between the reviewer and the tracks
 * ("Oops! Wrong Device", share-7). Case-insensitive, like the router's own matching.
 */
export function opensOnAnyDevice(pathname: string): boolean {
  return pathname.toLowerCase().startsWith("/review/");
}

export function useDeviceDetection() {
  // Lazy initializer: runs during the first render, before the first paint.
  const [isDesktop, setIsDesktop] = useState(detectDesktop);

  useEffect(() => {
    const handleResize = () => setIsDesktop(detectDesktop());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return { isDesktop };
}
